import React, { useMemo, useState } from 'react';
import { TaskBoardTask } from '../../types';
import { PRIORITY_COLORS, PRIORITY_LABELS, ProjectPriority, isTaskDone } from '../../utils/projectConstants';
import { formatArgentinaDate } from '../../utils/dateUtils';
import {
  formatAssigneeDisplay,
  getAssigneeAvatarStyle,
  getAssigneeInitialLetter,
} from '../../utils/projectAssignees';

interface ProjectListViewProps {
  tasks: TaskBoardTask[];
  onTaskClick: (task: TaskBoardTask) => void;
}

type SortKey = 'due_date' | 'priority' | 'created_at' | 'status';

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

const ProjectListView: React.FC<ProjectListViewProps> = ({ tasks, onTaskClick }) => {
  const [sortBy, setSortBy] = useState<SortKey>('due_date');
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'due_date') {
        cmp = (a.due_date || '9999').localeCompare(b.due_date || '9999');
      } else if (sortBy === 'priority') {
        cmp = (PRIORITY_ORDER[a.priority || 'medium'] ?? 2) - (PRIORITY_ORDER[b.priority || 'medium'] ?? 2);
      } else if (sortBy === 'created_at') {
        cmp = a.created_at.localeCompare(b.created_at);
      } else {
        cmp = a.status.localeCompare(b.status);
      }
      return sortAsc ? cmp : -cmp;
    });
    return copy;
  }, [tasks, sortBy, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) setSortAsc(!sortAsc);
    else { setSortBy(key); setSortAsc(true); }
  };

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <button type="button" onClick={() => toggleSort(field)} className={`text-[10px] font-semibold uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200 ${sortBy === field ? 'text-indigo-600' : 'text-gray-400'}`}>
      {label} {sortBy === field ? (sortAsc ? '↑' : '↓') : ''}
    </button>
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900/50">
      <div className="grid grid-cols-[1fr_100px_90px_90px_80px_36px] gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/80">
        <SortHeader label="Tarea" field="status" />
        <SortHeader label="Estado" field="status" />
        <SortHeader label="Prioridad" field="priority" />
        <SortHeader label="Vence" field="due_date" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Resp.</span>
        <span />
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[calc(100vh-260px)] overflow-y-auto">
        {sorted.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-12">No hay tareas que coincidan con los filtros</p>
        )}
        {sorted.map(task => {
          const priority = (task.priority || 'medium') as ProjectPriority;
          const done = isTaskDone(task.status, task.completed);
          const overdue = task.due_date && !done && new Date(task.due_date) < new Date(new Date().toDateString());

          return (
            <button
              key={task.id}
              type="button"
              onClick={() => onTaskClick(task)}
              className="w-full grid grid-cols-[1fr_100px_90px_90px_80px_36px] gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors items-center group"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PRIORITY_COLORS[priority] }} />
                <span className={`text-xs truncate ${done ? 'line-through text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {task.title}
                </span>
                {(task.tags || []).slice(0, 2).map(tag => (
                  <span key={tag} className="hidden sm:inline text-[9px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500">{tag}</span>
                ))}
              </div>
              <span className="text-[11px] text-gray-500 truncate">{task.status}</span>
              <span className="text-[11px] text-gray-500">{PRIORITY_LABELS[priority]}</span>
              <span className={`text-[11px] ${overdue ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                {task.due_date ? formatArgentinaDate(task.due_date) : '—'}
              </span>
              {task.assignee ? (
                <span
                  className="w-6 h-6 rounded-full text-[9px] font-semibold flex items-center justify-center"
                  style={getAssigneeAvatarStyle(task.assignee)}
                  title={formatAssigneeDisplay(task.assignee)}
                >
                  {getAssigneeInitialLetter(task.assignee)}
                </span>
              ) : <span className="text-gray-300">—</span>}
              <span className="text-gray-300 group-hover:text-indigo-500">→</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectListView;
